import { useState, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import {
  FaGlobe, FaBlog, FaInstagram, FaFacebook, FaTwitter,
  FaLinkedin, FaYoutube, FaWhatsapp, FaExternalLinkAlt,
  FaQuora, FaReddit
} from "react-icons/fa";
import logo from './assets/logo.svg'

interface SocialLink {
  name: string;
  url: string;
  icon: React.ElementType; // Updated to React.ElementType
  color: string;
  description: string;
}

const socialLinks: SocialLink[] = [
  {
    name: 'Website',
    url: 'https://eximpe.com/?utm_source=linktree&utm_medium=website&utm_campaign=social_links',
    icon: FaGlobe, // Pass the component directly
    color: 'bg-[#26a887]',
    description: 'Explore our world — official updates, services & more.'
  },
  {
    name: 'Blog',
    url: 'https://eximpe.com/blog/?utm_source=linktree&utm_medium=blog&utm_campaign=social_links',
    icon: FaBlog,
    color: 'bg-[#26a887]',
    description: 'Discover insights, stories, and expert articles.'
  },
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/exim.pe/#?utm_source=linktree&utm_medium=instagram&utm_campaign=social_links',
    icon: FaInstagram,
    color: 'bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500',
    description: 'See our journey through vibrant visuals & moments.'
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/eximpe/?utm_source=linktree&utm_medium=facebook&utm_campaign=social_links',
    icon: FaFacebook,
    color: 'bg-blue-600',
    description: 'Be part of our growing community and conversations.'
  },
  {
    name: 'Twitter',
    url: 'https://x.com/eximpe/?utm_source=linktree&utm_medium=twitter&utm_campaign=social_links',
    icon: FaTwitter,
    color: 'bg-black',
    description: 'Catch real-time news, thoughts & announcements.'
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/eximpe/?originalSubdomain=in&utm_source=linktree&utm_medium=linkedin&utm_campaign=social_links',
    icon: FaLinkedin,
    color: 'bg-blue-700',
    description: 'Network with us for professional news & opportunities.'
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@Eximpe1?utm_source=linktree&utm_medium=youtube&utm_campaign=social_links',
    icon: FaYoutube,
    color: 'bg-red-600',
    description: 'Watch our stories unfold — tutorials, insights & more.'
  },
  {
    name: 'WhatsApp',
    url: 'https://web.whatsapp.com/?utm_source=linktree&utm_medium=whatsapp&utm_campaign=social_links',
    icon: FaWhatsapp,
    color: 'bg-green-500',
    description: 'Chat with us directly — quick support & updates.'
  },
  {
    name: 'Quora',
    url: 'https://www.quora.com/profile/EximPe-1?utm_source=linktree&utm_medium=quora&utm_campaign=social_links',
    icon: FaQuora,
    color: 'bg-[#ba1c1b]',
    description: 'Explore answers, share knowledge, join the discussion.'
  },
  {
    name: 'Reddit',
    url: 'https://www.reddit.com/user/EximPe_Official/?utm_source=linktree&utm_medium=reddit&utm_campaign=social_links',
    icon: FaReddit,
    color: 'bg-[#ea580b]',
    description: 'Join the conversation, insights & community threads.'
  },
];

function SocialLinkCard({ link, index, hoveredLink, setHoveredLink, isLoaded, widthClass = 'w-full' }: { link: SocialLink; index: number; hoveredLink: number | null; setHoveredLink: React.Dispatch<React.SetStateAction<number | null>>; isLoaded: boolean; widthClass?: string }) {
  const Icon = link.icon; // Dynamically render the icon component
  return (
      <><Helmet>
      <title>Follow EximPe across Social Media – Stay Updated</title>
      <meta name="description" content="Stay updated with EximPe. Follow us on LinkedIn, Twitter, Facebook, and more for the latest news, updates, and insights on global trade." />
    </Helmet><a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`transform transition-all duration-500 hover:scale-105 ${widthClass} ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'} h-full`}
      style={{ transitionDelay: `${index * 100}ms` }}
      onMouseEnter={() => setHoveredLink(index)}
      onMouseLeave={() => setHoveredLink(null)}
    >
        <div className="flex flex-col h-full bg-white/80 backdrop-blur-md rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-300 border-2 border-white shadow-lg hover:shadow-xl group">
          <div className={`${link.color} p-6 flex justify-center items-center text-white relative h-28 overflow-hidden`}>
            <div className="relative z-10 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
              <Icon className="w-8 h-8" /> {/* Render the icon dynamically */}
            </div>
            <div
              className="absolute inset-0 opacity-90 transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background: `linear-gradient(45deg, ${link.color.replace('bg-', '')} 0%, transparent 100%)`
              }}
            ></div>
            {hoveredLink === index && (
              <FaExternalLinkAlt className="absolute bottom-2 right-2 w-5 h-5 text-white opacity-80" />
            )}
          </div>
          <div className="flex flex-col justify-between flex-grow p-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-800 mb-1">{link.name}</h3>
              <p className="text-gray-600 mb-3">{link.description}</p>
            </div>
          </div>
        </div>
      </a></>
  );
}

function App() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<number | null>(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <>
      <div className="relative min-h-screen bg-[#54ab9] text-white pt-24 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 w-full z-0" style={{ minHeight: '100%' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
            <circle cx="80%" cy="70%" r="300" fill="#e3f3ee" />
            <circle cx="10%" cy="35%" r="100" fill="#e3f3ee" />
          </svg>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <header className={`text-center mb-16 transform ${isLoaded ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-1000 ease-in-out`}>
            <div className="flex justify-center mb-6">
              <div className="backdrop-blur-lg p-5 rounded-full shadow-lg">
                <img src={logo} alt="EximPe Logo" className="w-16 h-16" />
              </div>
            </div>
            <h1 className="text-5xl font-bold text-gray-800 mb-4">Connect With EximPe</h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Stay connected with us through our various platforms and be part of our global network
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <div className="px-4 py-2 bg-[#27a887] backdrop-blur-sm rounded-full text-white shadow-md">
                <span>Instant E-FIRA Generation</span>
              </div>
              <div className="px-4 py-2 bg-[#27a887] backdrop-blur-sm rounded-full text-white shadow-md">
                <span>Zero FX-Markup</span>
              </div>
              <div className="px-4 py-2 bg-[#27a887] backdrop-blur-sm rounded-full text-white shadow-md">
                <span>100+ Countries</span>
              </div>
            </div>
          </header>

          <div className={`flex justify-center flex-wrap gap-6 mb-8 ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000 ease-in-out delay-300`}>
            {socialLinks
              .filter(link => link.name === 'Website' || link.name === 'Blog')
              .map((link, index) => (
                <SocialLinkCard
                  key={index}
                  link={link}
                  index={index}
                  hoveredLink={hoveredLink}
                  setHoveredLink={setHoveredLink}
                  isLoaded={isLoaded}
                  widthClass="md:w-[300px] w-[390px]"
                />
              ))}
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-1000 ease-in-out delay-300`}>
            {socialLinks
              .filter(link => link.name !== 'Website' && link.name !== 'Blog')
              .map((link, index) => (
                <SocialLinkCard
                  key={index}
                  link={link}
                  index={index}
                  hoveredLink={hoveredLink}
                  setHoveredLink={setHoveredLink}
                  isLoaded={isLoaded}
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default App;